import { AddableModuleType } from "@formant/core/model/AddableModuleType";
import type { ILayoutModule } from "@formant/core/model/ILayoutModule";
import type { IObserveLayout } from "@formant/core/model/IObserveLayout";
import type { ModuleType } from "@formant/core/model/ModuleType";

import { defaultModuleSizes } from "../component/Observe/defaultModuleSizes";

export interface IModuleInfo {
    name: string;
    type: AddableModuleType;
    id?: string;
}

export const getDefaultSize = (
    name: string,
    info: { [k: string]: ModuleType },
) =>
    (defaultModuleSizes[info[name]] ?? name.startsWith("custom-module:"))
        ? defaultModuleSizes.custom
        : { width: 2, height: 2 };

export const doesOverlap = (
    current: IObserveLayout,
    target: ILayoutModule,
): [boolean, number] => {
    const [r1Left, r1Top, r1Right, r1Bottom] = [
        target.x,
        target.y,
        target.x + target.width,
        target.y + target.height,
    ];

    const match = Object.values(current).find(bounds => {
        const [r2Left, r2Top, r2Right, r2Bottom] = [
            bounds.x,
            bounds.y,
            bounds.x + bounds.width,
            bounds.y + bounds.height,
        ];

        return !(
            r2Left >= r1Right ||
            r2Right <= r1Left ||
            r2Top >= r1Bottom ||
            r2Bottom <= r1Top
        );
    });

    return [!!match, match ? match.x + match.width - target.x : 0];
};

export const findAvailableSpot = (
    current: IObserveLayout,
    size: { width: number; height: number },
): { x: number; y: number } => {
    const position = { x: 0, y: 0 };
    let found = false;
    while (!found) {
        const [overlaps, next] = doesOverlap(current, { ...size, ...position });
        if (overlaps) {
            position.x += next;
            if (position.x + size.width > 12) {
                position.x = 0;
                position.y += 1;
            }
        }
        found = !overlaps;
    }
    return position;
};

export const layoutEnd = (layout: IObserveLayout): { x: number; y: number } => {
    const ends = Object.values(layout)
        .filter(element => !!element)
        .map(({ x, y, width, height }) => ({
            x: x + width,
            y: y + height,
        }));

    const endY = Math.max(...ends.map(_ => _.y), 0);
    const endX = Math.max(...ends.filter(_ => _.y === endY).map(_ => _.x), 0);
    return { x: endX, y: endY };
};

export function generateCompleteLayout(
    layout: IObserveLayout,
    streamInfos: IModuleInfo[],
): IObserveLayout {
    const activeLayout = Object.keys(layout)
        .filter(name => !!streamInfos.find(_ => _.name === name))
        .reduce<IObserveLayout>((acc, name) => {
            acc[name] = layout[name];
            return acc;
        }, {});

    const end = layoutEnd(layout);

    const generatedLayout = generateLayout(
        streamInfos.filter(({ name }) => !layout[name]),
        end,
    );

    return {
        ...activeLayout,
        ...generatedLayout,
    };
}
export const columns = 12;

function generateLayout(
    streamInfos: IModuleInfo[],
    start?: { x: number; y: number },
): IObserveLayout {
    let x = (start && start.x) || 0;
    let y = (start && start.y) || 0;
    let rowHeight = 0;
    const layout: IObserveLayout = {};

    for (const { name, type } of streamInfos) {
        if (layout[name]) {
            continue;
        }

        const { width, height } = defaultModuleSizes[type] || {
            width: 2,
            height: 2,
        };

        if (x + width > columns) {
            x = 0;
            y += rowHeight;
            rowHeight = 0;
        }

        layout[name] = {
            x,
            y,
            width,
            height,
        };

        x += width;
        rowHeight = Math.max(rowHeight, height);
    }

    return layout;
}
